import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Container,
    Title,
    Paper,
    Text,
    Badge,

    Stack,
    Alert,
    Card,
    Grid,
    Divider,
    Table,
    SimpleGrid,
    Button,

    Image,
    Avatar,
    Box,
    ThemeIcon,
    ActionIcon,
    Group,
    Menu,
} from '@mantine/core';
import {
    IconChevronDown,
    IconAlertCircle,
    IconCurrencyRupee,
    IconCalendar,
    IconUser,
    IconBriefcase,
    IconMail,
    IconPhone,
    IconClock,
    IconCheck,
    IconX,
    IconPrinter,
    IconTrendingUp,
    IconCash,
    IconShare,
    IconDownload,
} from '@tabler/icons-react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase/FirebaseCredential';
import { useAppSelector } from '../../hooks/hooks';
import { RootState } from '../../store/store';
import BenchBreadcrumbs from '../UIComponent/Components/BenchBreadcrumbs';
import BenchLoader from '../UIComponent/BenchLoader/BenchLoader';
import BenchSettingsBreadcrumbs from '../UIComponent/Components/BenchSettingsBreadcrumbs';
import BenchTopHeader from '../UIComponent/Components/BenchTopHeader';
import mypayrollreporticon from '../../assets/mypayrollreporticon.svg';
import BenchCard from '../UIComponent/Components/BenchCard';
// Types
interface CalculatedSalary {
    basic: number;
    conveyance_allowance: number;
    helper_allowance: number;
    house_rent_allowance: number;
    medical_allowance: number;
    monthly_ctc: number;
}

interface FixedMonthlyBreakdown {
    basic: number;
    conveyance_allowance: number;
    helper_allowance: number;
    house_rent_allowance: number;
    medical_allowance: number;
    monthly_ctc: number;
}

interface EmployeePayrollRecord {
    id: string;
    admin_id: string;
    branch_id: string;
    calculated_salary: CalculatedSalary;
    checkin_device_id: string;
    company_id: string;
    company_name: string;
    created_at: any;
    created_by: string;
    days_in_month: number;
    designation: string;
    email: string;
    first_name: string;
    fixed_monthly_breakdown: FixedMonthlyBreakdown;
    fixed_monthly_ctc: number;
    holiday_count: number;
    last_modified_by: string;
    last_modified_date: any;
    last_name: string;
    leave_days: number;
    mobile_number: string;
    monthly_ctc: string;
    name: string;
    non_working_days: number;
    paid_leave_count: number;
    present_days: number;
    status: string;
    total_days: number;
    total_payable_days: number;
    type: string;
    uid: string;
    payment_status?: string;
    payroll_period: string;
    month: string;
    year: number;
    pay_cycle_frequency: string;
    process_date: any;
}

export default function MyPayrollReport() {
    const companyId = useAppSelector((state: RootState) => state.session.company_id);
    const uid = useAppSelector((state: RootState) => state.session.uid);
    const [searchParams, setSearchParams] = useSearchParams();

    const [payrollRecords, setPayrollRecords] = useState<EmployeePayrollRecord[]>([]);
    const [pageLoading, setPageLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [activeTab, setActiveTab] = useState<string | null>(null);
    const [activeMonthTabs, setActiveMonthTabs] = useState<Record<string, string>>({});

    const breadcrumbLinks = [
        { label: 'My Payroll Report' }
    ];

    // Group records by year from created_at
    const recordsByYear = payrollRecords.reduce((acc, record) => {
        const createdDate = record.created_at?.toDate?.() || new Date();
        const year = createdDate.getFullYear().toString();
        if (!acc[year]) {
            acc[year] = [];
        }
        acc[year].push(record);
        return acc;
    }, {} as Record<string, EmployeePayrollRecord[]>);

    // Group records by year and month
    const recordsByYearAndMonth = payrollRecords.reduce((acc, record) => {
        const createdDate = record.created_at?.toDate?.() || new Date();
        const year = createdDate.getFullYear().toString();
        const month = record.month; // Use the month from the record

        if (!acc[year]) {
            acc[year] = {};
        }
        if (!acc[year][month]) {
            acc[year][month] = [];
        }
        acc[year][month].push(record);
        return acc;
    }, {} as Record<string, Record<string, EmployeePayrollRecord[]>>);

    // Get sorted years (most recent first)
    const availableYears = Object.keys(recordsByYear).sort((a, b) => parseInt(b) - parseInt(a));

    // Initialize year from URL params or set to most recent
    useEffect(() => {
        if (availableYears.length > 0 && activeTab === null) {
            const yearParam = searchParams.get('year');
            const validYear = yearParam && availableYears.includes(yearParam) ? yearParam : availableYears[0];
            setActiveTab(validYear);

            // Update URL if not set
            if (!yearParam) {
                const params = new URLSearchParams(searchParams);
                params.set('year', validYear);
                setSearchParams(params, { replace: true });
            }
        }
    }, [availableYears.length, activeTab]);

    // Get available months for active year
    const availableMonths = activeTab && recordsByYearAndMonth[activeTab]
        ? Object.keys(recordsByYearAndMonth[activeTab])
        : [];

    // Initialize month from URL params or set to first available
    useEffect(() => {
        if (activeTab && availableMonths.length > 0 && !activeMonthTabs[activeTab]) {
            const monthParam = searchParams.get('month');
            const validMonth = monthParam && availableMonths.includes(monthParam) ? monthParam : availableMonths[0];
            setActiveMonthTabs(prev => ({ ...prev, [activeTab]: validMonth }));

            // Update URL if not set
            if (!monthParam) {
                const params = new URLSearchParams(searchParams);
                params.set('year', activeTab);
                params.set('month', validMonth);
                setSearchParams(params, { replace: true });
            }
        }
    }, [activeTab, availableMonths.length, activeMonthTabs[activeTab || '']]);


    // Update URL when year tab changes
    const handleYearChange = (year: string | null) => {
        if (year) {
            setActiveTab(year);
            const params = new URLSearchParams(searchParams);
            params.set('year', year);
            params.delete('month'); // Will be set by month effect
            setSearchParams(params);
        }
    };

    // Update URL when month tab changes
    const handleMonthChange = (year: string, month: string) => {
        setActiveMonthTabs(prev => ({ ...prev, [year]: month }));
        const params = new URLSearchParams(searchParams);
        params.set('year', year);
        params.set('month', month);
        setSearchParams(params);
    };


    // Get current month's records
    const currentMonthRecords = activeTab && activeMonthTabs[activeTab] && recordsByYearAndMonth[activeTab]
        ? recordsByYearAndMonth[activeTab][activeMonthTabs[activeTab]] || []
        : [];

    // Calculate statistics for current month
    const totalEarnings = currentMonthRecords.reduce((sum, r) => sum + (r.calculated_salary?.monthly_ctc || 0), 0);
    const averageSalary = currentMonthRecords.length > 0 ? totalEarnings / currentMonthRecords.length : 0;

    // YTD calculation (current financial year: April to March)
    const currentDate = new Date();
    const currentFY = currentDate.getMonth() >= 3 ? currentDate.getFullYear() : currentDate.getFullYear() - 1;
    const ytdRecords = payrollRecords.filter(r => {
        const recordDate = r.process_date?.toDate?.() || new Date(0);
        const recordFY = recordDate.getMonth() >= 3 ? recordDate.getFullYear() : recordDate.getFullYear() - 1;
        return recordFY === currentFY;
    });
    const ytdEarnings = ytdRecords.reduce((sum, r) => sum + (r.calculated_salary?.monthly_ctc || 0), 0);

    // Print salary slip
    const printSalarySlip = (record: EmployeePayrollRecord) => {
        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        printWindow.document.write(`
            <html>
            <head>
                <title>Salary Slip - ${record.month} ${record.year}</title>
                <style>
                    body { font-family: Arial, sans-serif; padding: 20px; }
                    h1 { text-align: center; }
                    table { width: 100%; border-collapse: collapse; margin: 20px 0; }
                    th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                    th { background-color: #f2f2f2; }
                    .total { font-weight: bold; background-color: #e7f5ff; }
                </style>
            </head>
            <body>
                <h1>Salary Slip</h1>
                <p><strong>Employee:</strong> ${record.name || `${record.first_name} ${record.last_name}`}</p>
                <p><strong>Designation:</strong> ${record.designation}</p>
                <p><strong>Period:</strong> ${record.month} ${record.year}</p>
                <p><strong>Pay Period:</strong> ${record.payroll_period}</p>

                <h3>Attendance</h3>
                <table>
                    <tr><td>Total Days</td><td>${record.total_days}</td></tr>
                    <tr><td>Present Days</td><td>${record.present_days}</td></tr>
                    <tr><td>Leave Days</td><td>${record.leave_days}</td></tr>
                    <tr><td>Payable Days</td><td>${record.total_payable_days}</td></tr>
                </table>

                <h3>Salary Details</h3>
                <table>
                    <tr><th>Component</th><th>Fixed Amount</th><th>Calculated Amount</th></tr>
                    <tr><td>Basic Salary</td><td>₹${record.fixed_monthly_breakdown?.basic?.toFixed(2)}</td><td>₹${record.calculated_salary?.basic?.toFixed(2)}</td></tr>
                    <tr><td>HRA</td><td>₹${record.fixed_monthly_breakdown?.house_rent_allowance?.toFixed(2)}</td><td>₹${record.calculated_salary?.house_rent_allowance?.toFixed(2)}</td></tr>
                    <tr><td>Medical Allowance</td><td>₹${record.fixed_monthly_breakdown?.medical_allowance?.toFixed(2)}</td><td>₹${record.calculated_salary?.medical_allowance?.toFixed(2)}</td></tr>
                    <tr><td>Conveyance</td><td>₹${record.fixed_monthly_breakdown?.conveyance_allowance?.toFixed(2)}</td><td>₹${record.calculated_salary?.conveyance_allowance?.toFixed(2)}</td></tr>
                    <tr><td>Helper Allowance</td><td>₹${record.fixed_monthly_breakdown?.helper_allowance?.toFixed(2)}</td><td>₹${record.calculated_salary?.helper_allowance?.toFixed(2)}</td></tr>
                    <tr class="total"><td>Total</td><td>₹${record.fixed_monthly_ctc?.toFixed(2)}</td><td>₹${record.calculated_salary?.monthly_ctc?.toFixed(2)}</td></tr>
                </table>

                <p style="margin-top: 40px;"><em>This is a computer-generated document and does not require a signature.</em></p>
            </body>
            </html>
        `);
        printWindow.document.close();
        printWindow.print();
    };

    // Fetch employee's payroll records
    useEffect(() => {
        const fetchMyPayrollRecords = async () => {
            if (!companyId || !uid) {
                setError('User information not found. Please login again.');
                setPageLoading(false);
                return;
            }

            try {
                setPageLoading(true);

                // Get all payroll periods for the company
                const payrollRef = collection(db, 'companyprofile', companyId, 'payroll');
                const payrollSnapshot = await getDocs(payrollRef);

                const allRecords: EmployeePayrollRecord[] = [];

                // For each payroll period, fetch the employee's record
                for (const payrollDoc of payrollSnapshot.docs) {
                    const payrollData = payrollDoc.data();

                    const employeeRecordsRef = collection(
                        db,
                        'companyprofile',
                        companyId,
                        'payroll',
                        payrollDoc.id,
                        'employeepayrollrecords'
                    );

                    // Query for records matching the employee's UID
                    const q = query(employeeRecordsRef, where('uid', '==', uid));
                    const recordsSnapshot = await getDocs(q);

                    recordsSnapshot.forEach((doc) => {
                        allRecords.push({
                            id: doc.id,
                            ...doc.data(),
                            payroll_period: payrollData.pay_period || 'N/A',
                            month: payrollData.month || 'N/A',
                            year: payrollData.year || new Date().getFullYear(),
                            pay_cycle_frequency: payrollData.pay_cycle_frequency || 'N/A',
                            process_date: payrollData.process_date,
                        } as EmployeePayrollRecord);
                    });
                }

                // Sort by process date (most recent first)
                allRecords.sort((a, b) => {
                    const dateA = a.process_date?.toDate?.() || new Date(0);
                    const dateB = b.process_date?.toDate?.() || new Date(0);
                    return dateB.getTime() - dateA.getTime();
                });

                setPayrollRecords(allRecords);
                setError(null);
            } catch (err) {
                console.error('Error fetching payroll records:', err);
                setError('Failed to load your payroll records. Please try again later.');
            } finally {
                setPageLoading(false);
            }
        };

        fetchMyPayrollRecords();
    }, [companyId, uid]);

    const formatCurrency = (amount: number) => {
        return `₹${amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    };

    const formatDate = (timestamp: any) => {
        if (!timestamp) return 'N/A';
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        return date.toLocaleDateString('en-IN', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    };

    const formatEmployeeType = (type: string) => {
        if (!type) return 'N/A';
        // Convert underscore-separated to title case (e.g., 'company_admin' to 'Company Admin')
        return type
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    if (error) {
        return (
            <Container size="xl" py="xl">
                <BenchBreadcrumbs links={breadcrumbLinks} />
                <Alert icon={<IconAlertCircle size={16} />} title="Error" color="red" mt="md">
                    {error}
                </Alert>
            </Container>
        );
    }

    return (
        <div className="page">
            <Container size="lg" py="md">
                 <Box
            sx={(theme) => ({
              position: "sticky",
              top: 110, // Avoid overlapping the fixed navbar
              zIndex: 100,
              backgroundColor: "#FAFAFA",
              paddingTop: theme.spacing.sm,
              //paddingBottom: theme.spacing.xs,
              marginTop: `-${theme.spacing.sm}`,
            })}
          >
                <BenchSettingsBreadcrumbs links={breadcrumbLinks} />
                <Divider my="sm" /> </Box>
                <Stack spacing="lg">
                    {/* Breadcrumb */}


                    <BenchTopHeader
                        icon={<IconCash />}
                        title="My Payroll Reports"
                        description="Configure payroll tracking and policies for your organization"
                    />
                    {/* Employee Info Card */}
                    {payrollRecords.length > 0 && (
                        <BenchCard padding="xl" radius="lg" withBorder mt="lg">
                            <Group position="apart" align="center" spacing="xl">
                                {/* Profile Section */}
                                <Group spacing="lg">
                                    <Box
                                        sx={(theme) => ({
                                            backgroundColor: theme.colors.gray[0],
                                            borderRadius: theme.radius.md,
                                            padding: 12,
                                            position: 'relative',
                                        })}
                                    >
                                        <IconUser size={48} color="#adb5bd" stroke={1.5} />
                                        <Box
                                            sx={(theme) => ({
                                                position: 'absolute',
                                                bottom: -4,
                                                right: -4,
                                                backgroundColor: '#10b981',
                                                border: `2px solid ${theme.white}`,
                                                borderRadius: '50%',
                                                width: 16,
                                                height: 16,
                                            })}
                                        />
                                    </Box>
                                    <div>
                                        <Text size="lg" weight={700} style={{ fontFamily: 'Inter, sans-serif' }}>
                                            {payrollRecords[0].name ||
                                                `${payrollRecords[0].first_name} ${payrollRecords[0].last_name}`.trim()}
                                        </Text>
                                        <Group spacing={6} align="center">
                                            <Box
                                                sx={{
                                                    width: 6,
                                                    height: 6,
                                                    borderRadius: '50%',
                                                    backgroundColor: '#10b981',
                                                }}
                                            />
                                            <Text size="xs" color="dimmed" weight={500}>
                                                Active Employee
                                            </Text>
                                        </Group>
                                    </div>
                                </Group>

                                {/* Designation */}
                                <Group spacing="md">
                                    <ThemeIcon variant="light" color="blue" size={42} radius="md">
                                        <IconBriefcase size={22} stroke={1.5} />
                                    </ThemeIcon>
                                    <div>
                                        <Text size="xs" color="dimmed">Designation</Text>
                                        <Text size="sm" weight={600} color="dark">
                                            {payrollRecords[0].designation || 'N/A'}
                                        </Text>
                                    </div>
                                </Group>

                                {/* Email */}
                                <Group spacing="md">
                                    <ThemeIcon variant="light" color="grape" size={42} radius="md">
                                        <IconMail size={22} stroke={1.5} />
                                    </ThemeIcon>
                                    <div>
                                        <Text size="xs" color="dimmed">Email</Text>
                                        <Text size="sm" weight={600} color="dark">
                                            {payrollRecords[0].email || 'N/A'}
                                        </Text>
                                    </div>
                                </Group>

                                {/* Phone */}
                                <Group spacing="md">
                                    <ThemeIcon variant="light" color="orange" size={42} radius="md">
                                        <IconPhone size={22} stroke={1.5} />
                                    </ThemeIcon>
                                    <div>
                                        <Text size="xs" color="dimmed">Phone</Text>
                                        <Text size="sm" weight={600} color="dark">
                                            {payrollRecords[0].mobile_number || 'N/A'}
                                        </Text>
                                    </div>
                                </Group>
                            </Group>
                        </BenchCard>
                    )}

                    {/* Statistics Cards */}
                    {payrollRecords.length > 0 && (
                        <SimpleGrid cols={3} spacing="lg" breakpoints={[{ maxWidth: 'sm', cols: 1 }]}>
                            {/* YTD Earnings */}
                            <Card padding="lg" radius="md" withBorder style={{ borderTop: '4px solid #3b82f6' }}>
                                <Group position="apart" mb="xl">
                                    <ThemeIcon variant="light" color="blue" size="lg" radius="xl">
                                        <IconCash size={20} stroke={1.5} />
                                    </ThemeIcon>
                                    {/* <Badge variant="light" color="green" size="md" radius="sm">
                                        +12% vs last year
                                    </Badge> */}
                                </Group>
                                <Text size="sm" color="dimmed" weight={500} mb={4}>
                                    YTD Earnings
                                </Text>
                                <Text size={28} weight={700} style={{ lineHeight: 1 }}>
                                    {formatCurrency(ytdEarnings)}
                                </Text>
                            </Card>

                            {/* Total Earnings (Month) */}
                            <Card padding="lg" radius="md" withBorder style={{ borderTop: '4px solid #f97316' }}>
                                <Group position="apart" mb="xl">
                                    <ThemeIcon variant="light" color="orange" size="lg" radius="xl">
                                        <IconCurrencyRupee size={20} stroke={1.5} />
                                    </ThemeIcon>
                                </Group>
                                <Text size="sm" color="dimmed" weight={500} mb={4}>
                                    Total Earnings (Month)
                                </Text>
                                <Text size={28} weight={700} style={{ lineHeight: 1 }}>
                                    {formatCurrency(totalEarnings)}
                                </Text>
                                <Text size="xs" color="dimmed" mt={4}>
                                    {currentMonthRecords.length} record{currentMonthRecords.length !== 1 ? 's' : ''}
                                </Text>
                            </Card>

                            {/* Average Salary */}
                            <Card padding="lg" radius="md" withBorder style={{ borderTop: '4px solid #10b981' }}>
                                <Group position="apart" mb="xl">
                                    <ThemeIcon variant="light" color="green" size="lg" radius="xl">
                                        <IconCurrencyRupee size={20} stroke={1.5} />
                                    </ThemeIcon>
                                    <Badge variant="light" color="green" size="md" radius="sm">
                                        Stable
                                    </Badge>
                                </Group>
                                <Text size="sm" color="dimmed" weight={500} mb={4}>
                                    Average Salary
                                </Text>
                                <Text size={28} weight={700} style={{ lineHeight: 1 }}>
                                    {formatCurrency(averageSalary)}
                                </Text>
                            </Card>
                        </SimpleGrid>
                    )}

                    {/* Year Tabs with Month Tabs */}
                    {payrollRecords.length === 0 ? (
                        <Paper shadow="sm" p="xl" withBorder>
                            <Stack align="center" spacing="md">
                                <IconAlertCircle size={48} color="#868e96" />
                                <Text align="center" color="dimmed" size="lg">
                                    No payroll records found
                                </Text>
                                <Text align="center" color="dimmed" size="sm">
                                    Your payroll records will appear here once they are processed.
                                </Text>
                            </Stack>
                        </Paper>
                    ) : (
                        <>
                            <Group position="right" mb="md">
                                <Menu shadow="md" width={200}>
                                    <Menu.Target>
                                        <Card
                                            radius="lg"
                                            p={0}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                            }}
                                        >
                                            <Button
                                                variant="subtle"
                                                color="dark"
                                                size="sm"
                                                leftIcon={<IconCalendar size={16} />}
                                                rightIcon={<IconChevronDown size={14} />}
                                                styles={{
                                                    root: {
                                                        padding: '6px 12px',
                                                        height: 36,
                                                        '&:hover': {
                                                            backgroundColor: 'transparent',
                                                        },
                                                    },
                                                }}
                                            >
                                                {activeTab && activeMonthTabs[activeTab]
                                                    ? `${activeMonthTabs[activeTab]} ${activeTab}`
                                                    : 'Select Month'}
                                            </Button>
                                        </Card>
                                    </Menu.Target>

                                    <Menu.Dropdown style={{ maxHeight: 300, overflowY: 'auto' }}>
                                        {availableYears.map((year) => (
                                            <React.Fragment key={year}>
                                                <Menu.Label>{year}</Menu.Label>
                                                {recordsByYearAndMonth[year] &&
                                                    Object.keys(recordsByYearAndMonth[year]).map((month) => (
                                                        <Menu.Item
                                                            key={`${year}-${month}`}
                                                            onClick={() => {
                                                                handleYearChange(year);
                                                                handleMonthChange(year, month);
                                                            }}
                                                        >
                                                            {month}
                                                        </Menu.Item>
                                                    ))}
                                            </React.Fragment>
                                        ))}
                                    </Menu.Dropdown>
                                </Menu>
                            </Group>



                            {activeTab && activeMonthTabs[activeTab] && recordsByYearAndMonth[activeTab][activeMonthTabs[activeTab]]?.map((record) => (
                                <div key={record.id}>
                                    <BenchCard> <Stack spacing="md">

                                        <Card>
                                            <Group position="apart" align="center" style={{ padding: '8px 4px' }}>
                                                <div>
                                                    <Group spacing="xs" align="center">
                                                        <Text weight={700} size="lg" color="dark">
                                                            {record.month} Payroll
                                                        </Text>
                                                        <Badge color="blue" variant="light" size="sm" radius="md">
                                                            {record.pay_cycle_frequency}
                                                        </Badge>
                                                    </Group>
                                                    <Text size="sm" color="dimmed" mt={4}>
                                                        Processed on {formatDate(record.process_date)}
                                                    </Text>
                                                </div>

                                                <div style={{ textAlign: 'center' }}>
                                                    <Text size="sm" color="dimmed" weight={500}>Net Salary Credited</Text>
                                                    <Text weight={800} size="xl" color="#0ca678" style={{ fontSize: '28px', lineHeight: 1.2, fontFamily: 'Inter, sans-serif' }}>
                                                        {formatCurrency(record.calculated_salary?.monthly_ctc || 0)}
                                                    </Text>
                                                </div>

                                                <Group spacing="md">
                                                    <Button
                                                        leftIcon={<IconPrinter size={18} />}
                                                        color="red"
                                                        variant="filled"
                                                        radius="md"
                                                        size="sm"
                                                        onClick={() => printSalarySlip(record)}
                                                        styles={(theme) => ({
                                                            root: {
                                                                backgroundColor: '#f53e4f',
                                                                '&:hover': {
                                                                    backgroundColor: '#e03131'
                                                                }
                                                            }
                                                        })}
                                                    >
                                                        Print Payslip
                                                    </Button>
                                                </Group>
                                            </Group>
                                        </Card>
                                        <Grid gutter="lg" >
                                            {/* Column 1: Attendance Summary */}
                                            <Grid.Col xs={12} md={4}>
                                                <BenchCard p="lg" radius="md" withBorder style={{ height: '100%' }}>
                                                    <Group position="apart" mb="xl">
                                                        <Text weight={700} size="md">Attendance Summary</Text>
                                                        <Text size="xs" color="blue" style={{ cursor: 'pointer' }}>View Calendar</Text>
                                                    </Group>
                                                    <SimpleGrid cols={2} spacing="xl" py="lg" verticalSpacing="xl">
                                                        {[
                                                            { label: 'Total Days', value: record.total_days, icon: IconCalendar, color: 'blue' },
                                                            { label: 'Present', value: record.present_days, icon: IconCheck, color: 'green' },
                                                            { label: 'Leaves', value: record.leave_days, icon: IconX, color: 'red' },
                                                            { label: 'Payable', value: record.total_payable_days, icon: IconClock, color: 'violet' },
                                                            { label: 'Holidays', value: record.holiday_count, icon: IconCalendar, color: 'teal' },
                                                            { label: 'Paid Leaves', value: record.paid_leave_count, icon: IconCash, color: 'indigo' },
                                                            { label: 'Non-Working', value: record.non_working_days, icon: IconBriefcase, color: 'gray' },
                                                        ].map((item, index) => (
                                                            <Group key={index} spacing="sm" noWrap>
                                                                <ThemeIcon color={item.color} variant="light" size="lg" radius="md">
                                                                    <item.icon size={20} />
                                                                </ThemeIcon>
                                                                <div>
                                                                    <Text size="xs" color="dimmed" style={{ lineHeight: 1.2 }}>{item.label}</Text>
                                                                    <Text weight={500} ml="xs" size="lg" style={{ lineHeight: 1.2 }}>{item.value}</Text>
                                                                </div>
                                                            </Group>
                                                        ))}
                                                    </SimpleGrid>
                                                </BenchCard>
                                            </Grid.Col>

                                            {/* Column 2: Fixed Monthly CTC */}
                                            <Grid.Col xs={12} md={4}>
                                                <BenchCard p="lg" radius="md" withBorder style={{ height: '100%' }}>
                                                    <Text weight={700} size="md" mb={4}>Fixed Monthly CTC</Text>
                                                    <Text size="xs" color="dimmed" mb="xl">Standard salary structure</Text>

                                                    <Stack spacing="md">
                                                        <Group position="apart">
                                                            <Text size="sm" color="dimmed">Basic Salary</Text>
                                                            <Text size="sm" weight={600}>{formatCurrency(record.fixed_monthly_breakdown?.basic || 0)}</Text>
                                                        </Group>
                                                        <Group position="apart">
                                                            <Text size="sm" color="dimmed">House Rent Allowance (HRA)</Text>
                                                            <Text size="sm" weight={600}>{formatCurrency(record.fixed_monthly_breakdown?.house_rent_allowance || 0)}</Text>
                                                        </Group>
                                                        <Group position="apart">
                                                            <Text size="sm" color="dimmed">Medical Allowance</Text>
                                                            <Text size="sm" weight={600}>{formatCurrency(record.fixed_monthly_breakdown?.medical_allowance || 0)}</Text>
                                                        </Group>
                                                        <Group position="apart">
                                                            <Text size="sm" color="dimmed">Conveyance Allowance</Text>
                                                            <Text size="sm" weight={600}>{formatCurrency(record.fixed_monthly_breakdown?.conveyance_allowance || 0)}</Text>
                                                        </Group>
                                                        <Group position="apart">
                                                            <Text size="sm" color="dimmed">Special / Helper Allowance</Text>
                                                            <Text size="sm" weight={600}>{formatCurrency(record.fixed_monthly_breakdown?.helper_allowance || 0)}</Text>
                                                        </Group>

                                                        <Divider my="xs" />

                                                        <Group position="apart">
                                                            <Text size="sm" weight={700}>Total Gross Salary</Text>
                                                            <Text size="sm" weight={700}>{formatCurrency(record.fixed_monthly_ctc || 0)}</Text>
                                                        </Group>
                                                    </Stack>
                                                </BenchCard>
                                            </Grid.Col>

                                            {/* Column 3: Calculated Salary */}
                                            <Grid.Col xs={12} md={4}>
                                                <BenchCard p="lg" radius="md" withBorder style={{ height: '100%', backgroundColor: '#f8f9fa' }}>
                                                    <Group position="apart" mb={4}>
                                                        <Text weight={700} size="md">Calculated Salary</Text>
                                                        <Badge color="blue" variant="light">Final</Badge>
                                                    </Group>
                                                    <Text size="xs" color="dimmed" mb="xl">Based on {record.total_payable_days} payable days</Text>

                                                    <Stack spacing="md">
                                                        <Group position="apart">
                                                            <Text size="sm" color="dimmed">Basic Salary (Prorated)</Text>
                                                            <Text size="sm" weight={600}>{formatCurrency(record.calculated_salary?.basic || 0)}</Text>
                                                        </Group>
                                                        <Group position="apart">
                                                            <Text size="sm" color="dimmed">House Rent Allowance</Text>
                                                            <Text size="sm" weight={600}>{formatCurrency(record.calculated_salary?.house_rent_allowance || 0)}</Text>
                                                        </Group>
                                                        <Group position="apart">
                                                            <Text size="sm" color="dimmed">Medical Allowance</Text>
                                                            <Text size="sm" weight={600}>{formatCurrency(record.calculated_salary?.medical_allowance || 0)}</Text>
                                                        </Group>
                                                        <Group position="apart">
                                                            <Text size="sm" color="dimmed">Conveyance Allowance</Text>
                                                            <Text size="sm" weight={600}>{formatCurrency(record.calculated_salary?.conveyance_allowance || 0)}</Text>
                                                        </Group>
                                                        <Group position="apart">
                                                            <Text size="sm" color="dimmed">Special Allowance</Text>
                                                            <Text size="sm" weight={600}>{formatCurrency(record.calculated_salary?.helper_allowance || 0)}</Text>
                                                        </Group>

                                                        <Paper p="md" radius="md" style={{ backgroundColor: '#e6fcf5', marginTop: 16 }}>
                                                            <Group position="apart">
                                                                <Text weight={700} color="green.9">Net Payable</Text>
                                                                <Text weight={700} size="lg" color="green.9">{formatCurrency(record.calculated_salary?.monthly_ctc || 0)}</Text>
                                                            </Group>
                                                        </Paper>
                                                    </Stack>
                                                </BenchCard>
                                            </Grid.Col>
                                        </Grid>

                                        {/* Additional Info Footer */}
                                        <BenchCard p="md" radius="md" withBorder>
                                            <Group position="apart" align="center">

                                                <div>
                                                    <Text size="xs" color="dimmed">Status</Text>
                                                    <Badge color={record.status === 'Active' ? 'green' : 'gray'} variant="light" size="sm" mt={4}>
                                                        {record.status}
                                                    </Badge>
                                                </div>
                                                <div>
                                                    <Text size="xs" color="dimmed">Type</Text>
                                                    <Text size="sm" weight={600} mt={2}>{formatEmployeeType(record.type)}</Text>
                                                </div>
                                                <div>
                                                    <Text size="xs" color="dimmed">Days in Month</Text>
                                                    <Text size="sm" weight={600} mt={2}>{record.days_in_month}</Text>
                                                </div>

                                                <div>
                                                    <Text size="xs" color="dimmed" align="right">Payment Status</Text>
                                                    <Badge
                                                        color={
                                                            record.payment_status === 'Paid' ? 'green' :
                                                                record.payment_status === 'Processed' ? 'blue' :
                                                                    'orange'
                                                        }
                                                        variant="filled"
                                                        size="sm"
                                                        mt={4}
                                                    >
                                                        {record.payment_status || 'Pending'}
                                                    </Badge>

                                                </div>
                                                <div>
                                                    <Text size="xs" color="dimmed" align="right">Company</Text>
                                                    <Text size="sm" weight={600} align="right" mt={2}>{record.company_name || 'N/A'}</Text>
                                                </div>

                                            </Group>
                                        </BenchCard>
                                    </Stack>
                                    </BenchCard>
                                    {/* </BenchCard><Divider mt="sm" mb="xs"></Divider>
                                    <Divider /> */}

                                </div>
                            ))}
                        </>
                    )}
                </Stack>
            </Container>
            <BenchLoader isLoading={pageLoading} />
        </div>
    );
}
